import React from 'react'
import _ from 'lodash'
import { local } from 'data/lib/local-json'
import { isReportOnly } from 'data/lib/report-only'
import toggles from "feature-toggles"
import { navigate } from "@reach/router"
import notificationService from 'data/notification'

// keep synchronized with client_legacy/src/helpers/url-property-folder-check.js

// If redirect happens during load, use hard redirect; otherwise delegate to router.
const hard = (path) => window.location.pathname = path
const soft = (path) => path.startsWith('/n') ? navigate(path) : hard(path)
let redirect = hard // NB reset in component below

const Path = new class {
  _href = document.location.pathname
  didChange = () => this._href !== document.location.pathname
  update = () => this._href = document.location.pathname

  redirectIf() {
    const allowNoFolder = ['/', '/dashboard', '/n/dashboard', '/folder/explorer', '/n/folder/explorer', '/admin', '/n/admin/organization-setings', '/admin/users', '/n/admin', '/n/admin/users','/n/admin/user-groups', '/n/asset-management']
    const allowNoProperty = [...allowNoFolder, '/folder/settings', '/n/folder/settings', '/folder/property-list', '/n/folder/property-list', '/n/folder/reports', '/folder/underwriting-workbook', '/n/folder/underwriting-workbook', '/n/folder/snapshots', '/n/folder/import-export', '/folder/audit-log', '/n/folder/audit-log', '/n/asset-management']
    const disallowForReportOnly = ['/n/folder/settings', '/n/folder/underwriting-workbook', '/n/folder/import-export', '/n/folder/property-list', '/n/folder/reports']
    const pathIn = (paths) => paths.includes(document.location.pathname)
    const pathNotIn = (paths) => !paths.includes(document.location.pathname)

    const noFolder = _.isEmpty(_.filter(local('folderPath')))
    const noProperty = !_.isFinite(local('property'))
    const profile = local('auth.profile');
    const isAdminURL = document.location.pathname.startsWith('/n/admin')
    const isAssetManagmentURL = document.location.pathname.startsWith('/n/asset-management')
    const isAssetMode = 'asset' == local('appMode', null)

    if (isAdminURL) return
    if (isAssetMode && isAssetManagmentURL) return
    if (isAssetMode && !isAssetManagmentURL) return redirect('/n/asset-management')
    if (!isAssetMode && isAssetManagmentURL) return redirect('/folder/explorer')
    if (isReportOnly() && pathIn(disallowForReportOnly)) {
      notificationService.notify({
        type: "danger",
        message: "You are in Read-Only mode as this organization does not have an active rDCF subscription. Please enter a support ticket to receive information about our subscription plans.",
        dismiss: 3000
      })
      return redirect('/n/folder/explorer')
    }
    if (noProperty && pathNotIn(allowNoProperty) || noFolder && pathNotIn(allowNoFolder)) {
      if(toggles.displayFolderExplorer()) return redirect('/n/folder/explorer')
      else return redirect('/folder/explorer')
    }
    if(document.location.pathname == '/folder/import-export' && toggles.displayVTSImport()) {
      window.location.replace('/folder/import-export')
    }
    if (profile && profile.licensetype && profile.licensetype.name === 'essential' && document.location.pathname.endsWith('/n/folder/import-export')){
      if(toggles.displayFolderExplorer()) return redirect('/n/folder/explorer')
      else return redirect('/folder/explorer')
    }
  }

  check() {
    if (this.didChange()) {
      this.update()
      this.redirectIf()
    }
  }
}

const PropertyType = new class {
  _get = () => local('property_type', null)
  _type = local('property_type', null)
  _pathname = document.location.pathname
  didChange = () => this._type !== this._get() || this._pathname !== document.location.pathname
  update = () => {
    this._type = this._get();
    this._pathname = document.location.pathname
  }

  redirectIf() {
    const is = (s) => RegExp(s).test(document.location.pathname)
    const re = redirect
    const prefixIf = (cond, prefix='/n') => (ss) => (cond ? prefix : '') + ss[0]
    const am = prefixIf(toggles.displayAssetManagement())
    const tm = prefixIf(toggles.displayNewTenantMarket())
    const ni = prefixIf(toggles.displayNewInflation())
    const mfli = prefixIf(toggles.displayNewMFLineItems())
    const rr = prefixIf(toggles.displayNewRentRoll())
    const user = prefixIf(toggles.displayNewUserAdmin())
    const perm = prefixIf(toggles.permissions())

    const cases = {
      Commercial: [
        ['^/n/folder/settings',                          perm`/folder/settings`],
        ['^/n/property/settings',                        am`/property/settings`],
        ['^/n/hospitality/rooms',                        rr`/rent-roll/home`],
        ['^/n/hospitality/line-items',                   tm`/line-items/all`],
        ['^/n/hospitality/departmental-line-items',      tm`/line-items/all`],
        [`^(/n)?/multifamily/line-items`,                tm`/line-items/all`],
        [`^(/n)/(hospitality|multifamily)/area-measure`, tm`/property/area-measure`],
        [`^/n/property/area-measure`,                    tm`/property/area-measure`],
        [`^(/n)?/(hospitality|multifamily)/inflation`,   ni`/property-market/inflation`],
        [`^/n/multifamily/rent-roll`,                    rr`/rent-roll/home`],
        ['^(/n)?/admin/users',                           user`/admin/users`],
        ['^/n/admin/user-groups',                        toggles.displayNewUserAdmin() ? user`/admin/user-groups` : user`/admin/users`]
      ],
      Hospitality: [
        ['^/n/folder/settings',                                   perm`/folder/settings`],
        ['^/n/property/settings',                                 am`/property/settings`],
        [`^(/n)?/line-items`,                                     `/n/hospitality/line-items`],
        [`^/n/(multifamily|property-market)/inflation`,           `/n/hospitality/inflation`],
        [`^/n/multifamily/line-items`,                            `/n/hospitality/line-items`],
        [`^/n/multifamily/rent-roll`,                             `/n/hospitality/rooms`],
        [`^(/n)?/property-market`,                                `/n/hospitality/rooms`],
        [`^(/n)?/(recoveries|multifamily)/area-measure`,          `/n/hospitality/area-measure`],
        [`^(/n)?/recoveries`,                                     `/n/hospitality/rooms`],
        [`^(/n)?/rent-roll`,                                      `/n/hospitality/rooms`],
        [`^(/n)?/tenant-market`,                                  `/n/hospitality/rooms`],
        [`^(/n)?/property/defaults`,                              `/n/property/settings`],
        ['^(/n)?/admin/users',                                    user`/admin/users`],
        ['^/n/admin/user-groups',                                 toggles.displayNewUserAdmin() ? user`/admin/user-groups` : user`/admin/users`]
      ],
      Multifamily: [
        ['^/n/folder/settings',                                   perm`/folder/settings`],
        ['^/n/property/settings',                                 am`/property/settings`],
        ['^/n/hospitality/rooms',                                 '/n/multifamily/rent-roll'],
        ['^/n/hospitality/line-items',                            '/n/multifamily/line-items'],
        ['^/n/hospitality/departmental-line-items',               '/n/multifamily/line-items'],
        [`^(/n)?/line-items`,                                      mfli`/multifamily/line-items`],
        [`^(/n)?/(recoveries|hospitality)/area-measure`,          `/n/multifamily/area-measure`],
        [`^(/n)?/(property-market|hospitality)/inflation`,        `/n/multifamily/inflation`],
        [`^(/n)?/property-market`,                                `/n/multifamily/rent-roll`],
        [`^(/n)?/recoveries`,                                     `/n/multifamily/rent-roll`],
        [`^(/n)?/rent-roll`,                                      `/n/multifamily/rent-roll`],
        [`^(/n)?/tenant-market`,                                  `/n/multifamily/rent-roll`],
        ['^(/n)?/admin/users',                                    user`/admin/users`],
        ['^/n/admin/user-groups',                                 toggles.displayNewUserAdmin() ? user`/admin/user-groups` : user`/admin/users`]
      ]
    }

    if (!cases[this._type] && !(this._type === 'Mixed-Use')) return console.warn('Bad property type: ', this._type)
    for (let [pattern, path] of cases[this._type]) {
      if (is(pattern)) {
        return re(path)
      }
    }
  }

  check() {
    if (this.didChange()) {
      this.update()
      this.redirectIf()
    }
  }
}

const init = async () => {
  Path.redirectIf()
  await toggles.get()
  PropertyType.redirectIf()
  const bodyList = document.querySelector('body')
  const observer = new MutationObserver((mutations) => {
    Path.check()
    PropertyType.check()
  });
  const config = {
    childList: true,
    subtree: true
  };
  observer.observe(bodyList as Node, config);
}

export class UrlChecker extends React.Component {
  constructor(props) {
    super(props)
    redirect = soft
    init()
  }
  render() {
    return null
  }
}